import React, { useState, useMemo, useEffect } from "react";
import { StyleSheet, ScrollView, View, Pressable, Alert, TextInput, Platform } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import useAuth from "@/hooks/useAuth";
import { tenantService } from "@/services/tenantService";
import { DEFAULT_PASSWORD_REQUIREMENTS } from "@/types/db/tenants";

export default function UpdatePasswordScreen() {
  const { colors } = useTheme();
  const { updatePassword, user, isRecoverySession } = useAuth();
  const { recovery } = useLocalSearchParams<{ recovery?: string }>();
  const isRecovery = isRecoverySession || recovery === "true";

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantRequirements, setTenantRequirements] = useState(DEFAULT_PASSWORD_REQUIREMENTS);
  const [loadingRequirements, setLoadingRequirements] = useState(true);

  // Fetch tenant password requirements on mount
  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        const metaData = await tenantService.getTenantMetaData();
        if (metaData?.password_policy?.requirements) {
          setTenantRequirements({
            ...DEFAULT_PASSWORD_REQUIREMENTS,
            ...metaData.password_policy.requirements,
          });
        }
      } catch (err) {
        console.warn("Failed to fetch tenant password requirements, using defaults");
      } finally {
        setLoadingRequirements(false);
      }
    };
    fetchRequirements();
  }, []);

  // Password validation based on tenant requirements
  const requirements = useMemo(
    () => ({
      minLength: password.length >= tenantRequirements.min_length,
      hasUppercase: !tenantRequirements.require_uppercase || /[A-Z]/.test(password),
      hasLowercase: !tenantRequirements.require_lowercase || /[a-z]/.test(password),
      hasNumber: !tenantRequirements.require_number || /[0-9]/.test(password),
      hasSpecial: !tenantRequirements.require_special || /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }),
    [password, tenantRequirements],
  );

  const allRequirementsMet = Object.values(requirements).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const hasCurrentPassword = isRecovery || currentPassword.length > 0;
  const canSubmit = allRequirementsMet && passwordsMatch && hasCurrentPassword;

  const getInputBorderColor = (value: string, isValid: boolean) => {
    if (!value) return colors.border;
    return isValid ? colors.emerald?.border || colors.primary : colors.ruby?.border || "red";
  };

  const handleUpdatePassword = async () => {
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      await updatePassword(currentPassword, password, isRecovery);
      Alert.alert("Success", "Your password has been updated successfully.", [
        { text: "OK", onPress: () => router.replace("/") },
      ]);
    } catch (err: any) {
      setError(err?.message || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.content}>
        <ThemedText style={[styles.title, { color: colors.text }]}>Update Password</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          {isRecovery ? "Create a new secure password" : "Enter your current password and create a new secure password"}
        </ThemedText>

        {/* Password Inputs */}
        <View style={styles.formSection}>
          {/* Hidden email for browser password manager */}
          {Platform.OS === "web" && (
            <TextInput
              value={user?.email ?? ""}
              autoComplete="email"
              textContentType="username"
              readOnly
              style={{ position: "absolute", opacity: 0, height: 0, width: 0 }}
            />
          )}

          {/* Current Password (hidden in recovery mode) */}
          {!isRecovery && (
            <>
              <View style={styles.inputContainer}>
                <Input
                  icon="lock-open"
                  placeholder="Current Password"
                  value={currentPassword}
                  onChangeText={setCurrentPassword}
                  secureTextEntry={!showCurrentPassword}
                  autoComplete="current-password"
                  textContentType="password"
                  containerStyle={[
                    styles.input,
                    { borderColor: getInputBorderColor(currentPassword, hasCurrentPassword) },
                  ]}
                />
                <Pressable style={styles.eyeButton} onPress={() => setShowCurrentPassword(!showCurrentPassword)}>
                  <MaterialIcons
                    name={showCurrentPassword ? "visibility" : "visibility-off"}
                    size={20}
                    color={colors.textSecondary}
                  />
                </Pressable>
              </View>

              {/* Divider */}
              <View style={[styles.divider, { backgroundColor: colors.border }]} />
            </>
          )}

          {/* New Password */}
          <View style={styles.inputContainer}>
            <Input
              icon="lock"
              placeholder="New Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              autoComplete="new-password"
              textContentType="newPassword"
              containerStyle={[styles.input, { borderColor: getInputBorderColor(password, allRequirementsMet) }]}
            />
            <Pressable style={styles.eyeButton} onPress={() => setShowPassword(!showPassword)}>
              <MaterialIcons
                name={showPassword ? "visibility" : "visibility-off"}
                size={20}
                color={colors.textSecondary}
              />
            </Pressable>
          </View>

          {/* Confirm New Password */}
          <View style={styles.inputContainer}>
            <Input
              icon="lock"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={!showConfirmPassword}
              autoComplete="new-password"
              textContentType="newPassword"
              containerStyle={[styles.input, { borderColor: getInputBorderColor(confirmPassword, passwordsMatch) }]}
            />
            <Pressable style={styles.eyeButton} onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
              <MaterialIcons
                name={showConfirmPassword ? "visibility" : "visibility-off"}
                size={20}
                color={colors.textSecondary}
              />
            </Pressable>
          </View>

          {confirmPassword.length > 0 && !passwordsMatch && (
            <View style={styles.errorRow}>
              <MaterialIcons name="error" size={16} color={colors.ruby?.text || "red"} />
              <ThemedText style={[styles.errorText, { color: colors.ruby?.text || "red" }]}>
                Passwords do not match
              </ThemedText>
            </View>
          )}

          {error && (
            <View
              style={[
                styles.errorCard,
                { backgroundColor: colors.ruby?.background || "#fee2e2", borderColor: colors.ruby?.border || "red" },
              ]}
            >
              <MaterialIcons name="error" size={20} color={colors.ruby?.text || "red"} />
              <ThemedText style={[styles.errorCardText, { color: colors.ruby?.text || "red" }]}>{error}</ThemedText>
            </View>
          )}
        </View>

        {/* Requirements Card */}
        <View style={[styles.requirementsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.requirementsTitle, { color: colors.text }]}>Password Requirements</ThemedText>

          <RequirementRow
            met={requirements.minLength}
            text={`At least ${tenantRequirements.min_length} characters`}
            colors={colors}
          />
          {tenantRequirements.require_uppercase && (
            <RequirementRow met={requirements.hasUppercase} text="One uppercase letter" colors={colors} />
          )}
          {tenantRequirements.require_lowercase && (
            <RequirementRow met={requirements.hasLowercase} text="One lowercase letter" colors={colors} />
          )}
          {tenantRequirements.require_number && (
            <RequirementRow met={requirements.hasNumber} text="One number" colors={colors} />
          )}
          {tenantRequirements.require_special && (
            <RequirementRow met={requirements.hasSpecial} text="One special character (!@#$%^&*)" colors={colors} />
          )}
        </View>

        {/* Completion Section */}
        <View style={[styles.completionSection, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <MaterialIcons
            name="security"
            size={32}
            color={canSubmit ? colors.emerald?.text || colors.primary : colors.textSecondary}
          />
          <ThemedText style={[styles.completionTitle, { color: colors.text }]}>Secure Your Account</ThemedText>
          <ThemedText style={[styles.completionText, { color: colors.textSecondary }]}>
            Your new password will be updated immediately.
          </ThemedText>

          <Button
            text={submitting ? "Updating..." : "Update Password"}
            onPress={handleUpdatePassword}
            icon={submitting ? undefined : "check"}
            iconSize={18}
            iconColor="white"
            enabled={canSubmit && !submitting}
            style={[
              styles.submitButton,
              {
                backgroundColor:
                  canSubmit && !submitting ? colors.emerald?.background || colors.primary : colors.border,
                opacity: canSubmit && !submitting ? 1 : 0.5,
              },
            ]}
            textStyle={{ color: "white", fontSize: 16, fontWeight: "600" }}
          />
        </View>
      </View>
    </ScrollView>
  );
}

function RequirementRow({ met, text, colors }: { met: boolean; text: string; colors: any }) {
  return (
    <View style={styles.requirementRow}>
      <MaterialIcons
        name={met ? "check-circle" : "radio-button-unchecked"}
        size={18}
        color={met ? colors.emerald?.text || colors.primary : colors.textSecondary}
      />
      <ThemedText style={[styles.requirementText, { color: met ? colors.text : colors.textSecondary }]}>
        {text}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
  },
  formSection: {
    gap: 16,
    marginBottom: 24,
  },
  inputContainer: {
    position: "relative",
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
  },
  eyeButton: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    padding: 8,
  },
  divider: {
    height: 1,
    marginVertical: 8,
  },
  errorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  errorText: {
    fontSize: 14,
  },
  errorCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  errorCardText: {
    fontSize: 14,
    flex: 1,
  },
  requirementsCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 24,
    gap: 12,
  },
  requirementsTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  requirementRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  requirementText: {
    fontSize: 14,
  },
  completionSection: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
    alignItems: "center",
    gap: 12,
  },
  completionTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  completionText: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 8,
  },
  submitButton: {
    borderRadius: 8,
    minHeight: 48,
    minWidth: 200,
  },
});
